import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton, Variant } from '@indicia/component-library';
import useBem from '@indicia/use-bem';

import Statistics from '../../../features/dashboard/components/statistics/Statistics';
import useResultContext from '../../../features/dashboard/store/UseResultContext';
import Icon from '../button/enums/Icon';
import GLTBreadcrumb from '../glt-breadcrumb/GLTBreadcrumb';
import StatisticsButtonType from './enums/StatisticsButtonType';

import './DashboardBanner.scss';

type DashboardBannerProps = {
  inputAge: number;
  realAge: number;
  showParagraphAndTitleOnMobile?: boolean;
  statisticsButtonType?: StatisticsButtonType;
  onStatisticsButtonClick?: () => void;
  hasChanges?: boolean;
  title: ReactNode;
  paragraph: ReactNode;
  showBreadcrumb?: boolean;
  disableSticky?: boolean;
};

const DashboardBanner = ({
  inputAge,
  realAge,
  statisticsButtonType = undefined,
  onStatisticsButtonClick = undefined,
  hasChanges = false,
  showParagraphAndTitleOnMobile = true,
  title,
  paragraph,
  showBreadcrumb = true,
  disableSticky = false,
}: DashboardBannerProps) => {
  const { t } = useTranslation();
  const { bemClassName: bemClassNameBanner, bemClassNames: bemClassNamesBanner } = useBem('banner-container');
  const { lockedAge, result } = useResultContext();
  const [sticky, setSticky] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);

  useEffect(() => {
    const isSticky = () => {
      if (disableSticky) {
        setSticky(false);
        return;
      }

      const { scrollY, innerWidth } = window;
      const scrollTop = scrollY;

      // determine if compact mobile view (used to adjust threshold)
      // 150px threshold for desktop, 20px for compact mobile
      // 30px threshold for large tablets
      const isCompactMobile = innerWidth <= 768 && !showParagraphAndTitleOnMobile;
      let threshold = 150;
      if (innerWidth > 1270) {
        threshold = 30;
      } else if (isCompactMobile) {
        threshold = 10;
      }

      setSticky(scrollTop > threshold);
    };
    const onScroll = isSticky;
    window.addEventListener('scroll', onScroll);

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 1270);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [showParagraphAndTitleOnMobile, disableSticky]);

  const displayedTitle: ReactNode = hasChanges ? t('dashboard.banner.title-changed') : title;

  const delta = Math.round(lockedAge - (result.echteLeeftijd ?? 0));
  const ageDifference = Math.abs(delta);

  let displayedParagraph: ReactNode = paragraph;
  if (hasChanges) {
    if (delta < 0) {
      displayedParagraph = t('dashboard.banner.paragraph-increased', { ageDifference });
    } else if (delta > 0) {
      displayedParagraph = t('dashboard.banner.paragraph-decreased', { ageDifference });
    } else {
      displayedParagraph = paragraph;
    }
  }

  const renderStatisticsButton = () => {
    const isMail = statisticsButtonType === StatisticsButtonType.Mail;
    const isReset = statisticsButtonType === StatisticsButtonType.Reset;

    const shouldRender = isMail || (isReset && hasChanges);
    if (!shouldRender) return null;

    const id = `${statisticsButtonType}-button`;
    const label = isMail ? t('dashboard.change-your-age')! : t('dashboard.reset')!;

    return (
      <div className="undo-changes-button-container">
        <IconButton
          id={id}
          onClick={onStatisticsButtonClick}
          variant={Variant.Solid}
          iconName={Icon.arrowRight}
        >
          {label}
        </IconButton>
      </div>
    );
  };

  const willShowTitleOrParagraph = !isSmallScreen || showParagraphAndTitleOnMobile;
  const noTextOnMobile = isSmallScreen && !showParagraphAndTitleOnMobile;

  return (
    <div
      className={bemClassNamesBanner([
        'banner-root',
        bemClassNameBanner(null, 'sticky', sticky),
        bemClassNameBanner(null, 'no-text-mobile', noTextOnMobile),
      ])}
    >
      {!isSmallScreen && (
        <GLTBreadcrumb
          className={bemClassNameBanner('breadcrumb')}
          visible={showBreadcrumb}
        />
      )}
      {isSmallScreen && (
        <div className="statistics-container mobile">
          <div className="separator" />
          <Statistics
            age={inputAge}
            realAge={realAge}
            sticky={sticky}
          />
          {renderStatisticsButton()}
        </div>
      )}
      <div className={bemClassNamesBanner([bemClassNameBanner()])}>
        {willShowTitleOrParagraph && (
          <div className={bemClassNameBanner('text')}>
            {willShowTitleOrParagraph && (
              <h2
                className={bemClassNamesBanner([
                  bemClassNameBanner('title'),
                  bemClassNameBanner('title', 'changed', hasChanges),
                ])}
              >
                {displayedTitle}
              </h2>
            )}
            {willShowTitleOrParagraph && (
              <p
                className={bemClassNamesBanner([
                  bemClassNameBanner('paragraph'),
                  bemClassNameBanner('paragraph', 'changed', hasChanges),
                ])}
              >
                {displayedParagraph}
              </p>
            )}
          </div>
        )}
        {!isSmallScreen && (
          <div className="statistics-container">
            <Statistics
              age={inputAge}
              realAge={realAge}
              sticky={sticky}
            />
            {renderStatisticsButton()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardBanner;
